import { useCallback, useEffect, useRef, useState } from "react";
import {
  MARQUEE_MIN_SIZE_PX,
  normalizeMarqueeRect,
} from "../creativeTemplates/editor/canvasMarquee";
import { getPointerTimelineMetrics } from "./timelineInteractionUtils";
import { getTimelineRowsLayout } from "./timelineLayout";
import { getClipTimelineDuration } from "./projectSchema";
import {
  timeToTimelinePixels,
  VIDEO_EDITOR_TIMELINE_SCALE,
  VIDEO_EDITOR_TIMELINE_START_LEFT,
} from "./constants";

const clipIntersectsMarquee = ({ clip, row, marquee, scaleWidth }) => {
  if (!clip || !row || !marquee) {
    return false;
  }

  const start = Number(clip.position) || 0;
  const end = start + getClipTimelineDuration({ clip });
  const left = timeToTimelinePixels({
    time: start,
    scaleWidth,
    scale: VIDEO_EDITOR_TIMELINE_SCALE,
    startLeft: VIDEO_EDITOR_TIMELINE_START_LEFT,
  });
  const right = timeToTimelinePixels({
    time: end,
    scaleWidth,
    scale: VIDEO_EDITOR_TIMELINE_SCALE,
    startLeft: VIDEO_EDITOR_TIMELINE_START_LEFT,
  });
  const top = row.top;
  const bottom = row.top + row.height;

  return (
    left < marquee.x + marquee.w &&
    right > marquee.x &&
    top < marquee.y + marquee.h &&
    bottom > marquee.y
  );
};

const useTimelineMarqueeSelection = ({
  edl,
  scaleWidth,
  timelineAreaRef,
  enabled = true,
  onSelectClipIds = () => {},
}) => {
  const marqueeRef = useRef(null);
  const [marquee, setMarquee] = useState(null);

  const onTimelinePointerDown = useCallback(
    (event) => {
      if (!enabled || !edl || event.button !== 0) {
        return false;
      }

      const target = event.target;
      if (
        target?.closest?.("[data-video-editor-clip='true']") ||
        target?.closest?.("[data-video-editor-clip-menu='true']") ||
        target?.closest?.("[data-video-editor-track-add='true']") ||
        target?.closest?.("[data-video-editor-timeline-resize='true']") ||
        target?.closest?.(".timeline-editor-cursor") ||
        target?.closest?.(".timeline-editor-time-area")
      ) {
        return false;
      }

      const metrics = getPointerTimelineMetrics({
        timelineAreaNode: timelineAreaRef.current,
        clientX: event.clientX,
        clientY: event.clientY,
      });

      marqueeRef.current = {
        originX: metrics.xInContent,
        originY: metrics.offsetY,
        additive: Boolean(event.ctrlKey || event.metaKey || event.shiftKey),
        pointerId: event.pointerId,
        hasMoved: false,
      };
      return true;
    },
    [edl, enabled, timelineAreaRef]
  );

  useEffect(() => {
    const onMove = (event) => {
      const current = marqueeRef.current;
      if (!current) {
        return;
      }

      const metrics = getPointerTimelineMetrics({
        timelineAreaNode: timelineAreaRef.current,
        clientX: event.clientX,
        clientY: event.clientY,
      });
      const next = normalizeMarqueeRect({
        originX: current.originX,
        originY: current.originY,
        x: metrics.xInContent,
        y: metrics.offsetY,
      });
      if (next.w < MARQUEE_MIN_SIZE_PX && next.h < MARQUEE_MIN_SIZE_PX) {
        return;
      }
      current.hasMoved = true;
      if (current.raf) {
        return;
      }
      current.pending = next;
      current.raf = window.requestAnimationFrame(() => {
        current.raf = null;
        if (current.pending) {
          setMarquee(current.pending);
        }
      });
    };

    const onUp = (event) => {
      const current = marqueeRef.current;
      if (!current) {
        return;
      }
      if (current.raf) {
        window.cancelAnimationFrame(current.raf);
      }

      const metrics = getPointerTimelineMetrics({
        timelineAreaNode: timelineAreaRef.current,
        clientX: event.clientX,
        clientY: event.clientY,
      });
      const rect = normalizeMarqueeRect({
        originX: current.originX,
        originY: current.originY,
        x: metrics.xInContent,
        y: metrics.offsetY,
      });
      const didSelect = current.hasMoved && (rect.w >= MARQUEE_MIN_SIZE_PX || rect.h >= MARQUEE_MIN_SIZE_PX);
      marqueeRef.current = null;
      setMarquee(null);

      if (!didSelect) {
        return;
      }

      const layout = getTimelineRowsLayout({ edl });
      const hitIds = [];
      layout.rows.forEach((row) => {
        const track = row.trackId ? edl.tracks?.byId?.[row.trackId] : null;
        (track?.clipIds || []).forEach((clipId) => {
          const clip = edl.clips?.byId?.[clipId];
          if (
            clipIntersectsMarquee({
              clip,
              row,
              marquee: rect,
              scaleWidth,
            })
          ) {
            hitIds.push(clipId);
          }
        });
      });

      if (!hitIds.length) {
        return;
      }

      onSelectClipIds({ clipIds: hitIds, additive: current.additive });
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [edl, onSelectClipIds, scaleWidth, timelineAreaRef]);

  return {
    marquee,
    onTimelinePointerDown,
    isMarqueeActive: Boolean(marquee),
  };
};

export default useTimelineMarqueeSelection;
